import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { inventoryService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import { organizationService } from "../services/organizationService";

interface AddAssetFormProps {
  inventoryItemId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function AddAssetForm({
  inventoryItemId,
  onSuccess,
  onCancel,
}: AddAssetFormProps) {
  const { user, currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    barcode: "",
    serial_number: "",
    status: "available" as const,
    purchase_date: "",
    purchase_cost: "",
    supplier: "",
    order_number: "",
    warranty_end: "",
    notes: "",
  });

  const [error, setError] = useState("");
  const [barcode, setBarcode] = useState("");
  const [isGeneratingBarcode, setIsGeneratingBarcode] = useState(false);
  const [barcodeError, setBarcodeError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "" || !isNaN(Number(value))) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Update the generateBarcode function to provide better feedback
  const generateBarcode = async () => {
    if (!currentOrganization?.id) {
      setBarcodeError("Organization ID is required to generate a barcode");
      return;
    }

    try {
      setIsGeneratingBarcode(true);
      setBarcodeError("");

      const newBarcode = await organizationService.getNextAssetBarcode(
        currentOrganization.id
      );
      setBarcode(newBarcode);

      // Show a success message that fades out
      const barcodeInput = document.getElementById(
        "barcode"
      ) as HTMLInputElement;
      if (barcodeInput) {
        barcodeInput.classList.add("bg-green-50");
        setTimeout(() => {
          barcodeInput.classList.remove("bg-green-50");
        }, 1500);
      }
    } catch (error: any) {
      console.error("Error generating barcode:", error);
      setBarcodeError(error.message || "Failed to generate a unique barcode");
    } finally {
      setIsGeneratingBarcode(false);
    }
  };

  // Update the addAssetMutation to use the barcode state
  const addAssetMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("User not authenticated");
      if (!currentOrganization) throw new Error("No organization selected");

      try {
        console.log(
          `Creating asset for organization: ${currentOrganization.id}`
        );

        return await inventoryService.addAsset({
          barcode: barcode, // Use the barcode state instead of formData.barcode
          serial_number: formData.serial_number || null,
          parent_item_id: inventoryItemId,
          status: formData.status,
          purchase_date: formData.purchase_date || null,
          purchase_cost: formData.purchase_cost
            ? parseFloat(formData.purchase_cost)
            : null,
          supplier: formData.supplier || null,
          order_number: formData.order_number || null,
          warranty_end: formData.warranty_end || null,
          notes: formData.notes || null,
          created_by: user.id,
          updated_by: user.id,
          organization_id: currentOrganization.id, // Explicitly set the organization ID
        });
      } catch (error: any) {
        setError(error.message || "Failed to add asset");
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["inventory", inventoryItemId],
      });
      // Reset form on success
      setFormData({
        barcode: "",
        serial_number: "",
        status: "available",
        purchase_date: "",
        purchase_cost: "",
        supplier: "",
        order_number: "",
        warranty_end: "",
        notes: "",
      });
      setBarcode(""); // Reset barcode state
      if (onSuccess) onSuccess();
    },
  });

  // Replace the handleSubmit function with a fixed version
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError("");

    if (!barcode) {
      setError("Barcode is required");
      return;
    }

    // Use the existing addAssetMutation but with the barcode from state
    addAssetMutation.mutate();
  };

  return (
    <div className="bg-white rounded-lg">
      <h2 className="text-xl font-semibold mb-4">Add New Asset</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Barcode */}
          <div className="col-span-1 md:col-span-2">
            <label
              htmlFor="barcode"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Barcode*
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                type="text"
                name="barcode"
                id="barcode"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              />
              <button
                type="button"
                onClick={generateBarcode}
                disabled={isGeneratingBarcode}
                className="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isGeneratingBarcode ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Generating...
                  </span>
                ) : (
                  "Generate"
                )}
              </button>
            </div>
            {barcodeError && (
              <p className="mt-1 text-sm text-red-600">{barcodeError}</p>
            )}
          </div>

          {/* Serial Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Serial Number
            </label>
            <input
              type="text"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="available">Available</option>
              <option value="checked_out">Checked Out</option>
              <option value="maintenance">Maintenance</option>
              <option value="retired">Retired</option>
            </select>
          </div>

          {/* Purchase Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Date
            </label>
            <input
              type="date"
              name="purchase_date"
              value={formData.purchase_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Purchase Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Cost
            </label>
            <input
              type="text"
              name="purchase_cost"
              value={formData.purchase_cost}
              onChange={handleNumberChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="0.00"
            />
          </div>

          {/* Supplier */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Supplier
            </label>
            <input
              type="text"
              name="supplier"
              value={formData.supplier}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Order Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order Number
            </label>
            <input
              type="text"
              name="order_number"
              value={formData.order_number}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Warranty End */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Warranty End Date
            </label>
            <input
              type="date"
              name="warranty_end"
              value={formData.warranty_end}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={addAssetMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
            disabled={addAssetMutation.isPending}
          >
            {addAssetMutation.isPending ? "Saving..." : "Save Asset"}
          </button>
        </div>
      </form>
    </div>
  );
}
